import { z } from "zod";

export const personSchema = z.enum(["charlton", "david"]);
export type Person = z.infer<typeof personSchema>;
export const statusSchema = z.enum(["ready", "doing", "waiting", "done", "parked"]);
export const evidenceSchema = z.object({
  meetingId: z.string(),
  revision: z.string().optional(),
  segment: z.number().int().nonnegative(),
  quote: z.string().min(1),
  speaker: z.string(),
  seconds: z.number().nonnegative(),
});
export type Evidence = z.infer<typeof evidenceSchema>;
export const taskSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(180),
  brief: z.string().max(6000),
  streamId: z.string(),
  owner: personSchema,
  status: statusSchema,
  why: z.string().max(2000),
  completion: z.string().max(2000),
  dependsOn: z.array(z.string()),
  waitingFor: z.string().max(1000).nullable(),
  order: z.number().int(),
  evidence: z.array(evidenceSchema),
  updatedAt: z.string(),
  version: z.number().int(),
  correctedAt: z.string().nullable(),
  protectedFields: z.array(z.string()).optional(),
  pinnedAt: z.string().nullable().optional(),
  resumeAt: z.string().datetime().nullable().optional(),
  dueAt: z.string().datetime().nullable().optional(),
});
export type Task = z.infer<typeof taskSchema>;
export const streamSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(1).max(120),
  outcome: z.string().trim().min(1).max(3000),
  milestone: z.string().max(2000),
  tone: z.number().int(),
  state: z.enum(["active", "parked"]),
  order: z.number(),
  version: z.number().int().optional(),
  updatedAt: z.string().optional(),
});
export type Workstream = z.infer<typeof streamSchema>;
export const constraintSchema = z.object({
  id: z.string(),
  title: z.string().trim().min(1).max(180),
  owner: personSchema.nullable(),
  kind: z.enum(["priority", "capacity", "dependency", "decision"]),
  status: z.enum(["active", "resolved"]),
  impact: z.string().max(2500),
  nextStep: z.string().max(2000),
  resolvedWhen: z.string().max(2000),
  streamIds: z.array(z.string()),
  priorityStreamId: z.string().nullable(),
  evidence: z.array(evidenceSchema),
  version: z.number().int(),
  updatedAt: z.string(),
  correctedAt: z.string().nullable(),
});
export type Constraint = z.infer<typeof constraintSchema>;
export const segmentSchema = z.object({
  id: z.number().int().nonnegative(),
  speaker: z.string(),
  text: z.string(),
  start: z.number().nonnegative(),
  end: z.number().nonnegative().optional(),
});
export type Segment = z.infer<typeof segmentSchema>;
export const meetingSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  hash: z.string(),
  title: z.string(),
  occurredAt: z.string(),
  importedAt: z.string(),
  appName: z.string(),
  segments: z.array(segmentSchema),
  summary: z.string(),
  state: z.enum(["archived", "queued", "processing", "complete", "error"]),
  error: z.string().nullable(),
  processedSegments: z.number().int().nonnegative(),
});
export type Meeting = z.infer<typeof meetingSchema>;
export type MeetingSummary = Omit<Meeting, "segments" | "hash"> & { segmentCount: number };
export const eventSchema = z.object({
  id: z.string(),
  kind: z.enum(["created", "updated", "completed", "decision", "correction", "import", "system"]),
  title: z.string(),
  detail: z.string(),
  actor: z.string(),
  taskId: z.string().nullable(),
  meetingId: z.string().nullable(),
  createdAt: z.string(),
  previous: taskSchema.nullable(),
  previousTasks: z.array(taskSchema).optional(),
  afterVersions: z.record(z.string(), z.number().int()).optional(),
  evidence: z.array(evidenceSchema).optional(),
});
export type WorkEvent = z.infer<typeof eventSchema>;
export const focusSchema = z.object({
  title: z.string(),
  detail: z.string(),
  evidence: evidenceSchema.nullable(),
});
export type Focus = z.infer<typeof focusSchema>;
export type Workspace = {
  tasks: Task[];
  streams: Workstream[];
  meetings: MeetingSummary[];
  events: WorkEvent[];
  viewer: Person;
  timezone?: string;
  displayName?: string;
  inference?: { mode: "local" | "disabled"; status: "ready" | "waiting" | "disabled"; detail: string; processingLocation: string; cloudFallback: false };
  aiAvailable: boolean;
  model: string | null;
  localMode: boolean;
  focus: Focus;
  constraints: Constraint[];
  integration: { lastSeenAt: string | null; lastImportedAt: string | null; failures: number };
  historyCount: number;
  serverTime: string;
};
export const updateSchema = z.object({
  text: z.string().trim().min(2).max(10000),
  person: personSchema,
  requestId: z.string().uuid().optional(),
});
export const patchTaskSchema = z.object({
  version: z.number().int(),
  status: statusSchema.optional(),
  owner: personSchema.optional(),
  title: z.string().trim().min(1).max(180).optional(),
  note: z.string().max(2000).optional(),
  prioritize: z.boolean().optional(),
  brief: z.string().max(6000).optional(),
  completion: z.string().max(2000).optional(),
  streamId: z.string().min(1).optional(),
  dependsOn: z.array(z.string()).max(50).optional(),
  waitingFor: z.string().trim().max(1000).nullable().optional(),
  resumeAt: z.string().datetime().nullable().optional(),
  dueAt: z.string().datetime().nullable().optional(),
});
export const createTaskSchema = z.object({
  title: z.string().trim().min(1).max(180),
  brief: z.string().max(6000).default(""),
  streamId: z.string().min(1),
  owner: personSchema,
  completion: z.string().max(2000).default(""),
  dependsOn: z.array(z.string()).max(50).default([]),
  waitingFor: z.string().trim().max(1000).nullable().default(null),
  dueAt: z.string().datetime().nullable().optional(),
});
export const constraintInputSchema = constraintSchema
  .omit({ id: true, evidence: true, updatedAt: true, correctedAt: true, version: true })
  .extend({ version: z.number().int().optional() });
export const streamInputSchema = streamSchema
  .omit({ id: true, order: true, tone: true, version: true, updatedAt: true })
  .extend({ version: z.number().int().optional() });
export const ingestSchema = z
  .object({
    sourceId: z.string().max(200).optional(),
    title: z.string().max(300).optional(),
    occurredAt: z.string().datetime().optional(),
    appName: z.string().max(50).default("UltraMeet"),
    transcript: z.string().max(5_000_000).optional(),
    segments: z.array(segmentSchema).max(100000).optional(),
  })
  .refine(
    (v) => (v.transcript?.trim().length ?? 0) > 0 || v.segments !== undefined,
    "A transcript or segments are required",
  )
  .refine(
    (v) => !v.segments || new Set(v.segments.map((s) => s.id)).size === v.segments.length,
    "Transcript passage IDs must be unique",
  );
export type IngestInput = z.infer<typeof ingestSchema>;
export function ultraMeetSourceId(id: string): string {
  return /^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}$/.test(id) ? id : `push:${id}`;
}
export const PEOPLE: Record<Person, { name: string; role: string }> = {
  charlton: { name: "Charlton", role: "Engineering & delivery" },
  david: { name: "David", role: "Direction & commercial" },
};

export function blockers(task: Task, tasks: Task[]): Task[] {
  return task.dependsOn
    .map((id) => tasks.find((t) => t.id === id))
    .filter((t): t is Task => !!t && t.status !== "done");
}
export function isReady(task: Task, tasks: Task[]): boolean {
  return (
    ["ready", "doing"].includes(task.status) &&
    !task.waitingFor &&
    task.dependsOn.every((id) => tasks.some((t) => t.id === id)) &&
    blockers(task, tasks).length === 0
  );
}
export function nextTasks(
  tasks: Task[],
  person: Person,
  constraints: Constraint[] = [],
  streams: Workstream[] = [],
): Task[] {
  const priorityStreams = new Set(
    constraints
      .filter(
        (c) =>
          c.owner === person &&
          c.status === "active" &&
          c.kind === "priority" &&
          c.priorityStreamId,
      )
      .map((c) => c.priorityStreamId),
  );
  return tasks
    .filter(
      (t) =>
        t.owner === person &&
        isReady(t, tasks) &&
        (!streams.length || streams.find((s) => s.id === t.streamId)?.state === "active"),
    )
    .sort(
      (a, b) =>
        (b.pinnedAt ?? "").localeCompare(a.pinnedAt ?? "") ||
        Number(priorityStreams.has(b.streamId)) - Number(priorityStreams.has(a.streamId)) ||
        Number(!!b.dueAt && Date.parse(b.dueAt) <= Date.now() + 86400000) -
          Number(!!a.dueAt && Date.parse(a.dueAt) <= Date.now() + 86400000) ||
        Number(b.status === "doing") - Number(a.status === "doing") ||
        (Date.parse(a.dueAt ?? "") || Number.MAX_SAFE_INTEGER) -
          (Date.parse(b.dueAt ?? "") || Number.MAX_SAFE_INTEGER) ||
        a.order - b.order ||
        a.id.localeCompare(b.id),
    );
}
export const editableTaskFields = [
  "title",
  "brief",
  "streamId",
  "owner",
  "status",
  "why",
  "completion",
  "dependsOn",
  "waitingFor",
  "order",
  "resumeAt",
  "dueAt",
  "pinnedAt",
] as const;
export type EditableTaskField = (typeof editableTaskFields)[number];
export function protectedTaskFields(task: Task): string[] {
  return task.protectedFields ?? (task.correctedAt ? [...editableTaskFields] : []);
}
export function validateDependencies(tasks: Task[]): void {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const active = new Set<string>();
  const complete = new Set<string>();
  function visit(id: string): void {
    if (active.has(id)) throw new Error("BAD_REQUEST: A dependency would create a cycle");
    if (complete.has(id)) return;
    const task = byId.get(id);
    if (!task) throw new Error(`BAD_REQUEST: Unknown dependency: ${id}`);
    active.add(id);
    for (const dependency of task.dependsOn) visit(dependency);
    active.delete(id);
    complete.add(id);
  }
  for (const task of tasks) visit(task.id);
}
